import { lazy, Suspense, useMemo, memo, useState } from "react";
import { useHomepageDesign, getDesignFontStyles } from "@/hooks/useHomepageDesign";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getDesignById } from "@/data/homepageDesigns";
import LazySection from "@/components/LazySection";
import { SortedSections } from "@/components/SortedSections";
import { useSearchParams } from "react-router-dom";
import HeroBanner from "@/components/HeroBanner";
import HomeCategorySections from "@/components/HomeCategorySections";

import Design1 from "./Design1";

const Design2 = lazy(() => import("./Design2"));
const Design3 = lazy(() => import("./Design3"));
const Design4 = lazy(() => import("./Design4"));
const Design5 = lazy(() => import("./Design5"));
const Design6 = lazy(() => import("./Design6"));
const Design7 = lazy(() => import("./Design7"));
const Design8 = lazy(() => import("./Design8"));
const Design9 = lazy(() => import("./Design9"));
const Design10 = lazy(() => import("./Design10"));
const Design11 = lazy(() => import("./Design11"));
const Design12 = lazy(() => import("./Design12"));
const Design13 = lazy(() => import("./Design13"));
const Design14 = lazy(() => import("./Design14"));
const Design15 = lazy(() => import("./Design15"));
const Design16 = lazy(() => import("./Design16"));
const Design17 = lazy(() => import("./Design17"));
const Design18 = lazy(() => import("./Design18"));
const Design19 = lazy(() => import("./Design19"));
const Design20 = lazy(() => import("./Design20"));
const Design21 = lazy(() => import("./Design21"));
const Design22 = lazy(() => import("./Design22"));
const Design23 = lazy(() => import("./Design23"));
const Design24 = lazy(() => import("./Design24"));
const Design25 = lazy(() => import("./Design25"));
const Design26 = lazy(() => import("./Design26"));
const Design27 = lazy(() => import("./Design27"));
const Design28 = lazy(() => import("./Design28"));
const Design29 = lazy(() => import("./Design29"));
const Design30 = lazy(() => import("./Design30"));
const Design31 = lazy(() => import("./Design31"));
const Design32 = lazy(() => import("./Design32"));
const Design33 = lazy(() => import("./Design33"));
const Design34 = lazy(() => import("./Design34"));
const Design35 = lazy(() => import("./Design35"));
const Design36 = lazy(() => import("./Design36"));
const Design37 = lazy(() => import("./Design37"));
const Design38 = lazy(() => import("./Design38"));
const Design39 = lazy(() => import("./Design39"));
const Design40 = lazy(() => import("./Design40"));
const Design41 = lazy(() => import("./Design41"));
const Design42 = lazy(() => import("./Design42"));
const Design43 = lazy(() => import("./Design43"));
const Design44 = lazy(() => import("./Design44"));
const Design45 = lazy(() => import("./Design45"));
const Design46 = lazy(() => import("./Design46"));
const Design47 = lazy(() => import("./Design47"));
const Design48 = lazy(() => import("./Design48"));
const Design49 = lazy(() => import("./Design49"));
const Design50 = lazy(() => import("./Design50"));
const Design51 = lazy(() => import("./Design51"));
const Design52 = lazy(() => import("./Design52"));

const lazyDesigns: Record<number, ReturnType<typeof lazy>> = {
  2: Design2, 3: Design3, 4: Design4, 5: Design5, 6: Design6, 7: Design7,
  8: Design8, 9: Design9, 10: Design10, 11: Design11, 12: Design12, 13: Design13,
  14: Design14, 15: Design15, 16: Design16, 17: Design17, 18: Design18, 19: Design19,
  20: Design20, 21: Design21, 22: Design22, 23: Design23, 24: Design24, 25: Design25,
  26: Design26, 27: Design27, 28: Design28, 29: Design29, 30: Design30, 31: Design31,
  32: Design32, 33: Design33, 34: Design34, 35: Design35, 36: Design36, 37: Design37,
  38: Design38, 39: Design39, 40: Design40, 41: Design41, 42: Design42, 43: Design43,
  44: Design44, 45: Design45, 46: Design46, 47: Design47, 48: Design48, 49: Design49,
  50: Design50, 51: Design51, 52: Design52,
};

const SectionLoader = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
  </div>
);

const HomepageRenderer = memo(({ previewDesignId }: { previewDesignId?: number | null }) => {
  const { design, designId, loading } = useHomepageDesign();
  const { content, loading: contentLoading } = useSiteContent();
  const [homeSectionsVisible, setHomeSectionsVisible] = useState(false);
  const [searchParams] = useSearchParams();

  const showHero = searchParams.get("show_hero") === "1";
  const previewShowHeroParam = searchParams.get("preview_show_hero");
  const previewHeroEnabled = previewShowHeroParam === null ? null : previewShowHeroParam === "1";

  const resolvedDesignId = previewDesignId || designId || 1;
  const activeDesign = useMemo(() => getDesignById(resolvedDesignId), [resolvedDesignId]);

  const fontOverrides = {
    heading: content.font_heading || undefined,
    ui: content.font_ui || undefined,
    body: content.font_body || undefined,
  };

  const fontStyles = useMemo(
    () => getDesignFontStyles(activeDesign, fontOverrides),
    [activeDesign, fontOverrides.heading, fontOverrides.ui, fontOverrides.body]
  );

  const isLoading = !previewDesignId && (loading || contentLoading);
  const effectiveId = isLoading ? resolvedDesignId : resolvedDesignId;

  const homeCategorySections = (
    <LazySection height="600px" rootMargin="0px" onVisible={() => setHomeSectionsVisible(true)}>
      <Suspense fallback={null}>
        <HomeCategorySections enabled={homeSectionsVisible} />
      </Suspense>
    </LazySection>
  );

  const resolvedPreviewHero = previewDesignId ? (previewHeroEnabled ?? showHero) : true;

  // For non-Design1, show HeroBanner when explicitly enabled in preview or on the live site.
  const shouldShowHero = effectiveId !== 1 && resolvedPreviewHero;
  const hideInternalHero = !!previewDesignId && resolvedPreviewHero === false;

  // Each design renders its own HeroBanner internally; do not duplicate here.
  const heroBannerElement = null;

  if (effectiveId === 1) {
    return (
      <div style={fontStyles as any} className="homepage-design">
        <SortedSections>
          <Design1 />
          {homeCategorySections}
        </SortedSections>
      </div>
    );
  }

  const LazyComponent = lazyDesigns[effectiveId];
  if (!LazyComponent) {
    return (
      <div style={fontStyles as any} className="homepage-design">
        <SortedSections>
          <Design1 />
          {homeCategorySections}
        </SortedSections>
      </div>
    );
  }

  return (
    <div style={fontStyles as any} className={`homepage-design ${hideInternalHero ? "homepage-design-preview-no-hero" : ""}`}>
      {hideInternalHero && (
        <style>{`
          /* Hide first visual block (hero/banner) of any design when previewing with Hero OFF */
          .homepage-design-preview-no-hero .homepage-design-content > *:first-child,
          .homepage-design-preview-no-hero [data-hero="true"],
          .homepage-design-preview-no-hero section:first-of-type,
          .homepage-design-preview-no-hero > div > section:first-of-type,
          .homepage-design-preview-no-hero > div > div:first-child > section:first-of-type {
            display: none !important;
          }
        `}</style>
      )}
      {heroBannerElement}
      <SortedSections>
        <div className="homepage-design-content contents">
          <Suspense fallback={<SectionLoader />}>
            <LazyComponent />
          </Suspense>
        </div>
        {homeCategorySections}
      </SortedSections>
    </div>
  );
});

HomepageRenderer.displayName = "HomepageRenderer";

export default HomepageRenderer;
